/**
 * ImageCapture - Componente para captura de selfie via câmera
 * Abre a câmera frontal do dispositivo e permite tirar uma foto
 * Faz upload automático para o Supabase Storage
 */

import React, { useState, useRef, useCallback } from 'react';
import { Camera, X, Check, RefreshCw, Loader2, User } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface ImageCaptureProps {
    value?: string;
    onChange: (url: string) => void;
    userId: string;
    label?: string;
    placeholder?: string;
    error?: string;
    required?: boolean;
    useFrontCamera?: boolean;
}

export function ImageCapture({
    value,
    onChange,
    userId,
    label = 'Foto do Rosto',
    placeholder = 'Tire uma selfie',
    error,
    required = false,
    useFrontCamera = true
}: ImageCaptureProps) {
    const [isCapturing, setIsCapturing] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [capturedImage, setCapturedImage] = useState<string | null>(null);
    const [stream, setStream] = useState<MediaStream | null>(null);

    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    // Iniciar câmera
    const startCamera = useCallback(async () => {
        try {
            setIsCapturing(true);
            const mediaStream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: useFrontCamera ? 'user' : 'environment', // Frontal ou traseira
                    width: { ideal: 1280 },
                    height: { ideal: 720 }
                }
            });

            setStream(mediaStream);

            if (videoRef.current) {
                videoRef.current.srcObject = mediaStream;
                videoRef.current.play();
            }
        } catch (err) {
            console.error('Erro ao acessar câmera:', err);
            toast.error('Não foi possível acessar a câmera. Verifique as permissões.');
            setIsCapturing(false);
        }
    }, [useFrontCamera]);

    // Parar câmera
    const stopCamera = useCallback(() => {
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
            setStream(null);
        }
        setIsCapturing(false);
        setCapturedImage(null);
    }, [stream]);

    // Capturar foto
    const capturePhoto = useCallback(() => {
        if (!videoRef.current || !canvasRef.current) return;

        const video = videoRef.current;
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');

        if (!context) return;

        // Configurar canvas com tamanho do vídeo
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        // Espelhar horizontalmente apenas para câmera frontal (selfie)
        if (useFrontCamera) {
            context.translate(canvas.width, 0);
            context.scale(-1, 1);
        }

        // Desenhar frame do vídeo
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        // Converter para data URL
        const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
        setCapturedImage(dataUrl);

        // Parar stream de vídeo
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
            setStream(null);
        }
    }, [stream, useFrontCamera]);

    // Upload para Supabase
    const uploadPhoto = useCallback(async () => {
        if (!capturedImage) return;

        try {
            setIsUploading(true);

            // Verificar sessão atual do Supabase
            const { data: { session }, error: sessionError } = await supabase.auth.getSession();

            if (sessionError || !session) {
                console.error('Erro de sessão:', sessionError);
                toast.error('Sessão expirada. Faça login novamente.');
                return;
            }

            console.log('[ImageCapture] Sessão válida, userId:', session.user.id);

            // Converter data URL para Blob
            const response = await fetch(capturedImage);
            const blob = await response.blob();

            // Gerar nome único - usando userId na raiz (padrão do storage)
            const fileName = `selfie_${Date.now()}.jpg`;
            const filePath = `${session.user.id}/${fileName}`;

            console.log('[ImageCapture] Fazendo upload para:', filePath);

            // Upload para Supabase Storage
            const { data, error: uploadError } = await supabase.storage
                .from('Driver-documents')
                .upload(filePath, blob, {
                    contentType: 'image/jpeg',
                    cacheControl: '3600',
                    upsert: true
                });

            if (uploadError) {
                console.error('[ImageCapture] Erro no upload:', uploadError);
                throw uploadError;
            }

            console.log('[ImageCapture] Upload bem sucedido:', data);

            // Obter URL pública
            const { data: { publicUrl } } = supabase.storage
                .from('Driver-documents')
                .getPublicUrl(filePath);

            onChange(publicUrl);
            toast.success('Foto enviada com sucesso!');
            setCapturedImage(null);
            setIsCapturing(false);

        } catch (err: any) {
            console.error('Erro no upload:', err);
            toast.error(err?.message || 'Erro ao enviar foto. Tente novamente.');
        } finally {
            setIsUploading(false);
        }
    }, [capturedImage, onChange]);

    // Refazer foto
    const retakePhoto = useCallback(() => {
        setCapturedImage(null);
        startCamera();
    }, [startCamera]);

    return (
        <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
                {label} {required && <span className="text-red-500">*</span>}
            </label>

            {/* Preview da imagem salva */}
            {value && !isCapturing && (
                <div className="relative">
                    <div className="w-full aspect-square max-w-[200px] mx-auto rounded-2xl overflow-hidden border-2 border-emerald-200 shadow-lg">
                        <img
                            src={value}
                            alt="Selfie do motorista"
                            className="w-full h-full object-cover"
                        />
                    </div>
                    <button
                        type="button"
                        onClick={startCamera}
                        className="absolute -bottom-2 left-1/2 -translate-x-1/2 px-4 py-2 bg-emerald-500 text-white text-sm font-medium rounded-full shadow-lg hover:bg-emerald-600 transition-all flex items-center gap-2"
                    >
                        <RefreshCw className="w-4 h-4" />
                        Trocar foto
                    </button>
                </div>
            )}

            {/* Botão para iniciar captura */}
            {!value && !isCapturing && (
                <button
                    type="button"
                    onClick={startCamera}
                    className={`w-full p-6 border-2 border-dashed rounded-2xl transition-all flex flex-col items-center gap-3 ${error
                        ? 'border-red-300 bg-red-50 hover:border-red-400'
                        : 'border-gray-300 bg-gray-50 hover:border-emerald-400 hover:bg-emerald-50'
                        }`}
                >
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center ${error ? 'bg-red-100' : 'bg-emerald-100'
                        }`}>
                        <Camera className={`w-8 h-8 ${error ? 'text-red-500' : 'text-emerald-600'}`} />
                    </div>
                    <div className="text-center">
                        <p className={`font-medium ${error ? 'text-red-600' : 'text-gray-700'}`}>
                            {placeholder}
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                            Toque para abrir a câmera
                        </p>
                    </div>
                </button>
            )}

            {/* Área de captura */}
            {isCapturing && (
                <div className="relative bg-black rounded-2xl overflow-hidden">
                    {/* Vídeo da câmera */}
                    {!capturedImage && (
                        <>
                            <video
                                ref={videoRef}
                                autoPlay
                                playsInline
                                muted
                                className="w-full aspect-square object-cover transform scale-x-[-1]"
                            />

                            {/* Overlay com guia */}
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <div className="w-48 h-48 sm:w-56 sm:h-56 border-4 border-white/50 rounded-full" />
                            </div>

                            {/* Botões de controle */}
                            <div className="absolute bottom-4 left-0 right-0 flex items-center justify-center gap-4">
                                <button
                                    type="button"
                                    onClick={stopCamera}
                                    className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/30 transition-all"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                                <button
                                    type="button"
                                    onClick={capturePhoto}
                                    className="w-16 h-16 rounded-full bg-white flex items-center justify-center shadow-lg hover:scale-105 transition-all"
                                >
                                    <div className="w-12 h-12 rounded-full border-4 border-emerald-500" />
                                </button>
                                <div className="w-12 h-12" /> {/* Espaçador */}
                            </div>
                        </>
                    )}

                    {/* Preview da foto capturada */}
                    {capturedImage && (
                        <>
                            <img
                                src={capturedImage}
                                alt="Foto capturada"
                                className="w-full aspect-square object-cover"
                            />

                            {/* Botões de confirmação */}
                            <div className="absolute bottom-4 left-0 right-0 flex items-center justify-center gap-4">
                                <button
                                    type="button"
                                    onClick={retakePhoto}
                                    disabled={isUploading}
                                    className="px-5 py-3 rounded-full bg-white/20 backdrop-blur-sm flex items-center gap-2 text-white hover:bg-white/30 transition-all disabled:opacity-50"
                                >
                                    <RefreshCw className="w-5 h-5" />
                                    Refazer
                                </button>
                                <button
                                    type="button"
                                    onClick={uploadPhoto}
                                    disabled={isUploading}
                                    className="px-5 py-3 rounded-full bg-emerald-500 flex items-center gap-2 text-white shadow-lg hover:bg-emerald-600 transition-all disabled:opacity-50"
                                >
                                    {isUploading ? (
                                        <>
                                            <Loader2 className="w-5 h-5 animate-spin" />
                                            Enviando...
                                        </>
                                    ) : (
                                        <>
                                            <Check className="w-5 h-5" />
                                            Confirmar
                                        </>
                                    )}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* Canvas oculto para captura */}
            <canvas ref={canvasRef} className="hidden" />

            {/* Mensagem de erro */}
            {error && (
                <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                    <X className="w-4 h-4" />
                    {error}
                </p>
            )}
        </div>
    );
}

export default ImageCapture;
